import { HttpClient, HttpEvent, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EntityList } from 'app/models/entity-list.model';
import { map, Observable, Subject, tap } from 'rxjs';
import { Brand } from '../models/brand.model';
import { ExtendedModel, Model, PrintType } from '../models/model.model';
import { ExtendedSerie, Serie } from '../models/serie.model';
import { toHttpParams } from './http-query-params';

@Injectable({ providedIn: 'root' })
export class ModelService {
  private _http = inject(HttpClient);

  readonly updateModels = new Subject<void>();
  readonly updateBrand = new Subject<void>();
  readonly updateSeries = new Subject<void>();

  getModelById(modelId: number): Observable<{ statusCode: number; data: Model }> {
    return this._http.get<{ statusCode: number; data: Model }>('/api/model/' + modelId);
  }

  getModelList(query: ModelQuery = {}): Observable<{ statusCode: number; data: EntityList<ExtendedModel> }> {
    const route: Record<keyof ModelQuery, string> = {
      brandName: 'brand-name',
      serieName: 'serie-name',
      modelName: 'model-name',
      offset: 'offset',
      limit: 'limit',
      showParents: 'show-parents',
      serieIds: 'serie-ids',
      exclude: 'exclude',
      ids: 'ids',
      exactSearch: 'exact-search',
    };

    return this._http.get<{ statusCode: number; data: EntityList<ExtendedModel> }>('/api/model/list', {
      params: toHttpParams(query, route),
    });
  }

  addModel(model: { serieId: number; name: string; description: string; file: string | File }): Observable<HttpEvent<EmptyMutationBody>> {
    const newModel = Object.fromEntries(Object.entries(model).filter(([key, value]) => key === 'description' || value));

    return this._http.request<EmptyMutationBody>(
      new HttpRequest('POST', '/api/model', newModel, {
        reportProgress: true,
        headers: new HttpHeaders({ 'Content-Type': 'multipart/form-data' }),
      }),
    );
  }

  updateModel(
    modelId: number,
    modelInfo: { description: string; serieId: number; name: string; file?: File | string },
  ): Observable<HttpEvent<EmptyMutationBody>> {
    const updatedModel = Object.fromEntries(Object.entries(modelInfo).filter(([key, value]) => key === 'description' || value));

    return this._http.request<EmptyMutationBody>(
      new HttpRequest(
        'PUT',
        '/api/model',
        { ...updatedModel, id: modelId },
        {
          reportProgress: true,
          headers: new HttpHeaders({ 'Content-Type': 'multipart/form-data' }),
        },
      ),
    );
  }

  deleteModel(modelId: number): Observable<EmptyMutationResponse> {
    return this._http.delete('/api/model/' + modelId, { observe: 'response' }).pipe(
      map(toEmptyMutationResponse),
      tap(() => this.updateModels.next()),
    );
  }

  addSerie(data: { brandId: number; name: string }): Observable<EmptyMutationResponse> {
    return this._http.post('/api/serie', data, { observe: 'response' }).pipe(
      map(toEmptyMutationResponse),
      tap(() => this.updateSeries.next()),
    );
  }

  updateSerie(serieId: number, data: { brandId: number; name: string }): Observable<EmptyMutationResponse> {
    return this._http.put('/api/serie', { ...data, id: serieId }, { observe: 'response' }).pipe(
      map(toEmptyMutationResponse),
      tap(() => this.updateSeries.next()),
    );
  }

  deleteSerie(serieId: number): Observable<EmptyMutationResponse> {
    return this._http.delete('/api/serie/' + serieId, { observe: 'response' }).pipe(
      map(toEmptyMutationResponse),
      tap(() => this.updateSeries.next()),
    );
  }

  getSerieList(query: SerieQuery = {}): Observable<{ statusCode: number; data: EntityList<ExtendedSerie> }> {
    const route: Record<keyof SerieQuery, string> = {
      brandName: 'brand-name',
      serieName: 'serie-name',
      offset: 'offset',
      limit: 'limit',
      showParents: 'show-parents',
      brandIds: 'brand-ids',
      exactSearch: 'exact-search',
      exclude: 'exclude',
    };

    return this._http.get<{ statusCode: number; data: EntityList<ExtendedSerie> }>('/api/serie/list', {
      params: toHttpParams(query, route),
    });
  }

  getBrand(brandId: number): Observable<{ statusCode: number; data: Brand }> {
    return this._http.get<{ statusCode: number; data: Brand }>('/api/brand/' + brandId);
  }

  getSerie(serieId: number): Observable<{ statusCode: number; data: Serie }> {
    return this._http.get<{ statusCode: number; data: Serie }>('/api/serie/' + serieId);
  }

  getBrandList(query: BrandQuery = {}): Observable<{ statusCode: number; data: EntityList<Brand> }> {
    //const params = new HttpParams({ fromObject: query });
    const route: Record<keyof BrandQuery, string> = {
      brandName: 'brand-name',
      exactSearch: 'exact-search',
      offset: 'offset',
      limit: 'limit',
      exclude: 'exclude',
    };

    return this._http.get<{ statusCode: number; data: EntityList<Brand> }>('/api/brand/list', {
      params: toHttpParams(query, route),
    });
  }

  addBrand(newBrand: { name: string; file: string | File }): Observable<HttpEvent<EmptyMutationBody>> {
    const req = new HttpRequest('POST', '/api/brand', newBrand, {
      reportProgress: true,
      headers: new HttpHeaders({ 'Content-Type': 'multipart/form-data' }),
    });

    return this._http.request<EmptyMutationBody>(req);
  }

  editBrand(brandId: number, brand: { name: string; file?: File }): Observable<HttpEvent<EmptyMutationBody>> {
    return this._http.request<EmptyMutationBody>(
      new HttpRequest(
        'PUT',
        '/api/brand/',
        { ...brand, id: brandId },
        {
          reportProgress: true,
          headers: new HttpHeaders({ 'Content-Type': 'multipart/form-data' }),
        },
      ),
    );
  }

  deleteBrand(brandId: number): Observable<EmptyMutationResponse> {
    return this._http.delete('/api/brand/' + brandId, { observe: 'response' }).pipe(
      map(toEmptyMutationResponse),
      tap(() => this.updateBrand.next()),
    );
  }

  loadSVG(file: File, type: PrintType): Observable<any> {
    const myReader: FileReader = new FileReader();
    const subject = new Subject<any>();

    myReader.onload = () => {
      subject.next(this.parseSVG(myReader.result ?? '', type));
      subject.complete();
    };

    myReader.readAsText(file);

    return subject.asObservable();
  }
  private readonly DEBUG = false; // flip to true when troubleshooting SVG parsing

  parseSVG(svgstring: string | ArrayBuffer, type: PrintType) {
    const parser = new DOMParser();
    const svgDom = parser.parseFromString(svgstring as string, 'image/svg+xml');
    const docEle = <any>svgDom.documentElement;

    // Guard against SVGs with no <style> element
    const styleEl = docEle.getElementsByTagName('style').item(0);
    const ruleslist = <CSSRuleList>(styleEl?.sheet?.rules ?? []);

    // O(1) lookup by class name instead of O(n) filter per path
    const classToFill = new Map<string, string>();
    Array.from(ruleslist).forEach(rule => {
      if (rule instanceof CSSStyleRule) {
        classToFill.set(rule.selectorText.slice(1), rule.style.fill);
      }
    });

    const paths = docEle.querySelectorAll('path');
    const polygons = docEle.querySelectorAll('polygon');

    if (this.DEBUG) {
      console.log('[parseSVG] paths count:', paths.length);
      console.log('[parseSVG] polygons count:', polygons.length);
    }

    const usedLabels = new Set<string>();
    const pathsInfo: PathInfo[] = [];

    paths.forEach((p: Element, index: number) => {
      const d = p.getAttribute('d');
      pathsInfo.push(this.buildPathInfo(p.getAttribute('id'), p.getAttribute('class'), d, index, type, classToFill, usedLabels));
    });

    // Polygons — converted to path data, indexed after existing paths
    polygons.forEach((p: Element, polygonIndex: number) => {
      const points = p.getAttribute('points');
      const pathD = this.convertPolygonPointsToPath(points);
      const index = paths.length + polygonIndex;
      pathsInfo.push(this.buildPathInfo(p.getAttribute('id'), p.getAttribute('class'), pathD, index, type, classToFill, usedLabels));
    });

    if (this.DEBUG) {
      console.log('[parseSVG] pathsInfo final', pathsInfo);
    }

    return pathsInfo;
  }

  /**
   * Builds a single normalized path/polygon info entry, assigning
   * a fallback label and guaranteeing label uniqueness across the document.
   */
  private buildPathInfo(
    id: string | null,
    className: string | null,
    pathD: string | null,
    index: number,
    type: PrintType,
    classToFill: Map<string, string>,
    usedLabels: Set<string>,
  ): PathInfo {
    const pathInfo: PathInfo = {
      label: id ?? '',
      path: pathD,
      class: className,
      color: classToFill.get(className ?? ''),
    };

    if (this.DEBUG && (!id || !pathInfo.color)) {
      console.log('[parseSVG] anomaly', { index, id, className, color: pathInfo.color });
    }

    const baseLabel = id ?? (type === PrintType.WINDOW ? `noname-${index + 1}` : `no-label-${index + 1}`);

    if (!id) {
      pathInfo.class = type === PrintType.WINDOW ? 'noname' : 'no-label';
      if (type === PrintType.WINDOW) {
        pathInfo.color = 'rgb(0,0,0)';
      }
    }

    let uniqueLabel = baseLabel;
    let increment = 2;
    while (usedLabels.has(uniqueLabel)) {
      uniqueLabel = `${baseLabel}-${increment++}`;
    }

    pathInfo.label = uniqueLabel;
    usedLabels.add(uniqueLabel);

    return pathInfo;
  }

  private convertPolygonPointsToPath(points: string | null): string {
    if (!points) return '';

    // Parse points string: "x1,y1 x2,y2 x3,y3" or "x1 y1 x2 y2 x3 y3"
    const pointArray = points.split(/[\s,]+/).filter(p => p.trim());
    if (pointArray.length < 2) return '';

    const pathParts = [];
    for (let i = 0; i < pointArray.length; i += 2) {
      const x = pointArray[i];
      const y = pointArray[i + 1];
      const cmd = i === 0 ? 'M' : 'L';
      pathParts.push(`${cmd}${x},${y}`);
    }
    pathParts.push('Z'); // Close the path

    return pathParts.join(' ');
  }
}

type PathInfo = {
  label: string;
  path: string | null;
  class: string | null;
  color: string | undefined;
};

export type BrandQuery = {
  brandName?: string;
  limit?: number;
  offset?: number;
  exactSearch?: boolean;
  exclude?: number[];
};

export type SerieQuery = {
  brandName?: string;
  serieName?: string;
  offset?: number;
  limit?: number;
  showParents?: boolean;
  brandIds?: number[];
  exactSearch?: boolean;
  exclude?: number[];
};

export type ModelQuery = {
  brandName?: string;
  serieName?: string;
  modelName?: string;
  offset?: number;
  limit?: number;
  showParents?: boolean;
  serieIds?: number[];
  ids?: number[];
  exactSearch?: boolean;
  exclude?: number[];
};

type EmptyMutationBody = { statusCode: number; data: null };
type EmptyMutationResponse = { statusCode: number };

const toEmptyMutationResponse = (response: HttpResponse<unknown>): EmptyMutationResponse => ({ statusCode: response.status });
